import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as Astronaut } from 'assets/svgs/astro.svg';
import { ReactComponent as NoItemsSvg } from 'assets/svgs/no-items.svg';
import { Button } from 'components/atoms/button';
import { Checkbox as CheckboxAtom } from 'components/atoms/checkbox';
import { EmptyState as EmptyStateAtom } from 'components/atoms/empty-state';
import { SearchInput } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import {
  Caption01,
  ProductiveHeading05,
  ProductiveHeading06,
} from 'components/styles';
import { RoutePaths } from 'constant';
import {
  ROLES_QUERY_KEY,
  ROLES_WITH_PLACEHOLDER_QUERY_KEY,
  useMakeRolesAvailableForPartnersMutation,
  useVendorRolesQuery,
} from 'queries/roles';
import { sortByText } from 'utils/sort-by-text';

const EmptyState = styled(EmptyStateAtom)`
  height: 100%;
  justify-content: flex-end;
  color: var(--gray-70);
  padding: var(--spacing-05) 0;

  ${EmptyStateAtom.Message} {
    text-align: start;
    padding-left: var(--spacing-04);
  }
`;
const Header = styled.div`
  ${ProductiveHeading06};
  width: 85%;
  font-weight: var(--heading-4-weight);
  margin-bottom: var(--spacing-08);
`;
const Label = styled.div`
  ${Caption01};
  color: var(--gray-80);
`;
const Checkbox = styled(CheckboxAtom)`
  display: flex;
  align-items: center;
`;
const Body = styled.div`
  padding: var(--spacing-07) var(--spacing-07) 0;
  ${Label} {
    margin-bottom: var(--spacing-05);
  }
`;
const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 232px;
`;
const Row = styled.div`
  padding: var(--spacing-04);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-05);
  ${Checkbox} {
    flex-shrink: 0;
  }
`;
const RoleList = styled.div`
  margin-top: var(--spacing-05);
  height: 232px;
  overflow-y: auto;
  ${Row}:nth-child(2n - 1) {
    background: var(--gray-10);
  }
`;
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-05);
  padding: var(--spacing-05);
  border-top: 1px solid var(--border-color);
`;

interface MainContentProps {
  onClose: () => void;
  onRoleMigrationDone: () => void;
}
const MainContent: FC<MainContentProps> = ({
  onClose,
  onRoleMigrationDone,
}) => {
  const [searchText, setSearchText] = useState('');
  const { mutate: makeRolesAvailableForPartners, isLoading } =
    useMakeRolesAvailableForPartnersMutation();
  const { data: roles, isLoading: isRoleLoading } = useVendorRolesQuery();
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>(
    roles?.map(({ roleId }) => roleId) ?? [],
  );
  const queryClient = useQueryClient();
  const rolesForSelection = useMemo(
    () =>
      roles
        ?.map(({ roleId, roleName }) => ({
          roleId,
          roleName,
          selected: selectedRoleIds.includes(roleId),
        }))
        .sort(({ roleName: roleNameA }, { roleName: roleNameB }) =>
          sortByText(roleNameA, roleNameB),
        ) ?? [],
    [roles, selectedRoleIds],
  );
  const filteredRoles = useMemo(
    () =>
      rolesForSelection.filter(({ roleName }) =>
        roleName.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [rolesForSelection, searchText],
  );
  const isDisplayedRolesSelected = useMemo(
    () =>
      filteredRoles.length > 0 &&
      !filteredRoles.some(({ selected }) => !selected),
    [filteredRoles],
  );
  const onSearchInputChange = useCallback(
    (e) => setSearchText(e.target.value),
    [],
  );
  const onSelectAllChange = useCallback(
    (event) => {
      const isChecked = event.target.checked;

      if (isChecked) {
        setSelectedRoleIds((prev) =>
          prev.concat(
            filteredRoles
              .filter(({ roleId }) => !prev.includes(roleId))
              .map(({ roleId }) => roleId),
          ),
        );
      } else {
        const roleIds = filteredRoles.map(({ roleId }) => roleId);

        setSelectedRoleIds((prev) =>
          prev.filter((roleId) => !roleIds.includes(roleId)),
        );
      }
    },
    [filteredRoles],
  );
  const onRoleSelectionChange = useCallback((role) => {
    return (event) => {
      const isChecked = event.target.checked;

      if (isChecked) {
        setSelectedRoleIds((prev) => prev.concat([role.roleId]));
      } else {
        setSelectedRoleIds((prev) => prev.filter((x) => x !== role.roleId));
      }
    };
  }, []);
  const onAdd = useCallback(() => {
    makeRolesAvailableForPartners(selectedRoleIds, {
      onSuccess: () => {
        onRoleMigrationDone();
        queryClient.invalidateQueries(ROLES_QUERY_KEY);
        queryClient.invalidateQueries(ROLES_WITH_PLACEHOLDER_QUERY_KEY);
      },
    });
  }, [
    makeRolesAvailableForPartners,
    onRoleMigrationDone,
    queryClient,
    selectedRoleIds,
  ]);

  return (
    <MainContentWrapper>
      <Body>
        <Header>
          <FormattedMessage id="partners.role_selection.modal.title" />
        </Header>
        <Row>
          <Checkbox
            checked={isDisplayedRolesSelected}
            onChange={onSelectAllChange}
            disabled={filteredRoles.length === 0}
          >
            <FormattedMessage id="partners.role_selection.select_all_roles" />
          </Checkbox>
          <SearchInput
            direction="right"
            size="small"
            searchButtonProps={{ type: 'text' }}
            value={searchText}
            onChange={onSearchInputChange}
          />
        </Row>
        <RoleList>
          {isRoleLoading ? (
            <LoadingWrapper>
              <Loading css />
            </LoadingWrapper>
          ) : filteredRoles.length === 0 ? (
            <EmptyState
              image={<NoItemsSvg />}
              message={
                <FormattedMessage id="resource_management.no_users_or_placeholder_matched" />
              }
            />
          ) : (
            <>
              {filteredRoles.map((role) => (
                <Row key={role.roleId}>
                  <Checkbox
                    checked={role.selected}
                    onChange={onRoleSelectionChange(role)}
                  >
                    {role.roleName}
                  </Checkbox>
                </Row>
              ))}
            </>
          )}
        </RoleList>
      </Body>
      <Footer>
        <Link to={RoutePaths.rolesSettings}>
          <Button type="link">
            <FormattedMessage id="partners.role_selection.link.manage_role_here" />
          </Button>
        </Link>
        <div>
          <Button type="text" onClick={onClose} disabled={isLoading}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            type="primary"
            disabled={selectedRoleIds.length === 0}
            onClick={onAdd}
            loading={isLoading}
          >
            <FormattedMessage
              id="partners.role_selection.add_roles.button"
              values={{ count: selectedRoleIds.length }}
            />
          </Button>
        </div>
      </Footer>
    </MainContentWrapper>
  );
};

const SideBarWrapper = styled.div`
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  background: var(--gray-10);
`;
const SideBarContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--spacing-07);
  gap: var(--spacing-05);
`;
const Title = styled.div`
  ${ProductiveHeading05};
`;
const Message = styled.div``;

const SideBarContent: FC = () => (
  <SideBarWrapper>
    <SideBarContentWrapper>
      <Title>
        <FormattedMessage id="partners.role_selection.modal.sidebar.title" />
      </Title>
      <Message>
        <FormattedMessage id="partners.role_selection.modal.sidebar.message" />
      </Message>
    </SideBarContentWrapper>
    <Astronaut />
  </SideBarWrapper>
);

const ModalContent = styled.div`
  display: flex;
  height: 494px;

  ${SideBarWrapper} {
    width: 246px;
    flex-shrink: 0;
  }
`;

interface ModalProps {
  onClose: () => void;
  visible: boolean;
  onRoleMigrationDone: () => void;
}

export const PartnerRolesSelectionModal: FC<ModalProps> = ({
  onClose,
  visible,
  onRoleMigrationDone,
}) => {
  return (
    <Modal
      visible={visible}
      title={null}
      onCancel={onClose}
      width={814}
      footer={null}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
    >
      {visible ? (
        <ModalContent>
          <SideBarContent />
          <MainContent
            onClose={onClose}
            onRoleMigrationDone={onRoleMigrationDone}
          />
        </ModalContent>
      ) : null}
    </Modal>
  );
};
